<script setup lang="ts">
import { useDefaultStore } from "@/store";
import type { IPost } from "src/pages/index/blog/[slug]/index.vue";

const store = useDefaultStore();
const router = useRouter();
const blogPosts = ref<IPost[] | null>(null);
const blogPost = ref<IPost | null>(null);
const blogTypeString = ref<string | null>(null);
const displayedPostIndex = ref(0);

const props = withDefaults(
	defineProps<{
		blogType: string;
	}>(),
	{
		blogType: "regular",
	},
);

async function changeType(type: string) {
	blogTypeString.value = type;
	setInitialPost();
}

const academyPageName = computed(() =>
	blogTypeString.value === "legal" ? "LegalAcademy" : "RegularAcademy",
);

const currentBltId = computed(() => (blogTypeString.value === "legal" ? 1 : 2));

const postsOfCurrentType = computed(
	() =>
		blogPosts.value?.filter((post) => post.blt_id === currentBltId.value) || [],
);

function setInitialPost() {
	blogPost.value = postsOfCurrentType.value[0] || null;
}

function nextPost(direction: "l" | "r") {
	if (blogPosts.value && Array.isArray(blogPosts.value)) {
		const currentBltId = blogTypeString.value === "legal" ? 1 : 2;
		const postsOfCurrentType = blogPosts.value.filter(
			(post) => post.blt_id === currentBltId,
		);

		if (postsOfCurrentType.length > 0) {
			if (direction === "r") {
				displayedPostIndex.value =
					(displayedPostIndex.value + 1) % postsOfCurrentType.length;
			} else if (direction === "l") {
				displayedPostIndex.value =
					(displayedPostIndex.value - 1 + postsOfCurrentType.length) %
					postsOfCurrentType.length;
			}
			blogPost.value = postsOfCurrentType[displayedPostIndex.value];
		}
	}
}

async function toArticle(item: IPost) {
	if (item.blt_id == 2) {
		await router.push({
			name: "PostPage",
			params: { slug: item.pst_slug },
		});
	} else if (item.blt_id == 1) {
		await router.push({
			name: "LegalPostPage",
			params: { slug: item.pst_slug },
		});
	}
}

onMounted(async () => {
	blogTypeString.value = props.blogType;
	blogPosts.value = await store.getSsPostsForHomePage();
	setInitialPost();
});
</script>

<template lang="pug">
.academy-wrapper(
	v-if="blogPosts && blogPosts.length > 0 && postsOfCurrentType && blogPost"
)
	router-link.title(:to="{ name: academyPageName }", aria-label="See all") {{ $t("app.academy.title") }}
	.switch-wrapper
		.switch-container
			.left-option(
				:class="{ 'active-option': blogTypeString == 'legal' }",
				@click="changeType('legal')"
			) {{ $t("app.blogTypeLegal") }}
			.right-option(
				:class="{ 'active-option': blogTypeString == 'regular' }",
				@click="changeType('regular')"
			) {{ $t("app.blogTypeRegular") }}
	.content-wrapper(
		:style="{ 'background-image': 'url(' + blogPost.img_image + ')' }"
	)
		router-link.info-container(
			:to="{ name: blogPost.blt_id === 2 ? 'PostPage' : 'LegalPostPage', params: { slug: blogPost.pst_slug } }"
		)
			.post-title(:title="blogPost.pst_title") {{ blogPost.pst_title }}
			.post-description {{ blogPost.pst_description }}
		.buttons-wrapper
			.left(@click="nextPost('l')")
				img.icon(
					src="@/assets/image/icons/arrow-left.svg",
					alt="icon",
					loading="lazy"
				)
			.right(@click="nextPost('r')")
				img.icon(
					src="@/assets/image/icons/arrow-right.svg",
					alt="icon",
					loading="lazy"
				)

	.info-container-sm
		router-link.post-title(
			:title="blogPost.pst_title",
			:to="{ name: blogPost.blt_id === 2 ? 'PostPage' : 'LegalPostPage', params: { slug: blogPost.pst_slug } }"
		) {{ blogPost.pst_title }}
		.post-description {{ blogPost.pst_description }}
	.button-wrapper-academy
		router-link.home-page-white-button(
			:to="{ name: academyPageName }",
			aria-label="See all"
		) {{ $t("app.seeAll").toLocaleUpperCase() }}
</template>

<style lang="scss" scoped>
.academy-wrapper {
	padding: 24px 0 0;
	position: relative;

	.button-wrapper-academy {
		display: none;
		position: absolute;
		right: 24px;
		top: 24px;
	}
	.title {
		font-family: Pelinka, sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 24px;
		color: $app-dark-color;
		margin-bottom: 24px;
		text-align: center;
		text-transform: uppercase;
		cursor: pointer;
		text-decoration: none;
		display: block;
	}

	.switch-wrapper {
		display: flex;
		justify-content: center;
		align-items: center;

		.switch-container {
			display: flex;
			border: 2px solid $app-accent-color1;
			padding: 2px;
			width: 200px;
			cursor: pointer;
			margin-bottom: 24px;
			height: 36px;

			.left-option,
			.right-option {
				font-family: Pelinka, sans-serif;
				font-size: 12px;
				line-height: 18px;
				font-weight: 700;
				text-align: left;
				height: 100%;
				width: 50%;
				display: flex;
				justify-content: center;
				align-items: center;
				color: $app-accent-color1;
			}

			.left-option {
				&.active-option {
					background-color: $app-accent-color1;
					color: $app-dark-color;
				}
			}

			.right-option {
				&.active-option {
					background-color: $app-accent-color1;
					color: $app-dark-color;
				}
			}
		}
	}

	.content-wrapper {
		width: 100%;
		aspect-ratio: 100 / 45;
		background-repeat: no-repeat;
		background-position: center;
		background-size: cover;
		position: relative;

		.info-container {
			text-decoration: none;
			display: none;
			position: absolute;
			top: 100%;
			left: 0;
			width: 100%;
			padding: 24px;
			background-color: $text-color-lighter;
			box-sizing: border-box;
			cursor: pointer;

			.post-title {
				font-family: Pelinka, sans-serif;
				font-weight: 700;
				font-size: 18px;
				line-height: 24px;
				color: $app-dark-color;
				margin-bottom: 24px;
				text-transform: uppercase;
			}

			.post-description {
				@include ellipsis;

				-webkit-line-clamp: 4;
				font-family: "Galano Grotesque Alt", sans-serif;
				font-size: 16px;
				font-weight: 500;
				line-height: 20px;
				height: calc(20px * 4 + 1px);
			}
		}

		.buttons-wrapper {
			position: absolute;
			bottom: 24px;
			right: 24px;
			display: flex;
			.left,
			.right {
				width: 35px;
				height: 35px;
				display: flex;
				justify-content: center;
				align-items: center;
				cursor: pointer;

				.icon {
					width: 20%;
				}
			}

			.left {
				border: 1px solid rgba(0, 0, 0, 0);

				&:hover {
					border: 1px solid $app-dark-color;
					background-color: $app-accent-color1;
				}
			}

			.right {
				background-color: $app-accent-color1;
				border: 1px solid $app-accent-color1;

				&:hover {
					border: 1px solid $app-dark-color;
				}
			}
		}
	}

	.info-container-sm {
		text-decoration: none;
		width: 100%;
		padding: 24px;
		background-color: $text-color-lighter;
		box-sizing: border-box;
		cursor: pointer;

		.post-title {
			text-decoration: none;
			font-family: Pelinka, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 24px;
			color: $app-dark-color;
			margin-bottom: 24px;
			text-transform: uppercase;
		}

		.post-description {
			@include ellipsis;

			-webkit-line-clamp: 4;
			font-family: "Galano Grotesque Alt", sans-serif;
			font-size: 16px;
			font-weight: 500;
			line-height: 20px;
			height: calc(20px * 4 + 1px);
		}
	}
}

@media screen and (width >= 760px) {
	.academy-wrapper {
		.button-wrapper-academy {
			display: block;
			position: absolute;
			right: 24px;
			top: 50px;
		}
		.title {
			font-size: 30px;
			line-height: 38px;
			margin: 26px 0 50px;
		}

		.switch-wrapper {
			.switch-container {
				width: 300px;
				height: 50px;

				.left-option,
				.right-option {
					font-size: 16px;
				}
			}
		}
		.content-wrapper {
			margin-bottom: 0;
			display: block;
			.info-container {
				top: unset;
				bottom: 12%;
				left: 0;
				width: 50%;
				padding: 24px;
				display: flex;
				flex-direction: column;
				justify-content: center;

				.post-title {
					font-size: 30px;
					line-height: 38px;
					margin-bottom: 12px;
				}

				.post-description {
					font-size: 18px;
					line-height: 22px;
					-webkit-line-clamp: 2;
					height: calc(22px * 2 + 3px);
				}
			}

			.buttons-wrapper {
				bottom: 12%;
			}
		}
		.info-container-sm {
			display: none;
		}
	}
}

@media screen and (width >= 1024px) {
	.academy-wrapper {
		padding: 85px 0;
		.button-wrapper-academy {
			display: block;
			position: absolute;
			right: 85px;
			top: 85px;
		}
		.title {
			font-size: 40px;
			line-height: 46px;
			max-width: 1180px;
			margin: 0 auto 85px;
		}

		.switch-wrapper {
			.switch-container {
				width: 484px;
				margin-bottom: 85px;
				height: 85px;

				.left-option,
				.right-option {
					font-size: 22px;
					line-height: 25px;
				}
			}
		}

		.content-wrapper {
			.info-container {
				width: 50%;
				padding: 24px 85px;
				display: flex;
				flex-direction: column;
				justify-content: center;

				.post-title {
					font-size: 40px;
					line-height: 46px;
					margin-bottom: 24px;
				}

				.post-description {
					font-size: 22px;
					line-height: 27px;
					margin-bottom: 0;
					height: calc(27px * 2 + 3px);
				}
			}

			.buttons-wrapper {
				bottom: 12%;
				right: 85px;
				.left,
				.right {
					width: 70px;
					height: 70px;
				}
			}
		}
	}
}

@media screen and (width >= 1572px) {
	.academy-wrapper {
		.title {
			margin-bottom: 100px;
		}

		.switch-wrapper {
			.switch-container {
				width: 484px;
				margin-bottom: 85px;
				height: 85px;

				.left-option,
				.right-option {
					font-size: 22px;
					line-height: 25px;
				}
			}
		}

		.content-wrapper {
			display: block;
			.info-container {
				width: 887px;
				padding: 24px 85px;
			}

			.buttons-wrapper {
				bottom: 12%;
			}
		}
	}
}
</style>
